import Head from 'next/head';
import { NextPage } from 'next';
import { useTranslation } from 'next-i18next';

import type { EditorListing } from 'src/common/types/api/listings';
import type { AuthUserApiResponse } from 'src/common/types/api/v4/auth/user';
import AdminPagesLayout from 'src/admin/common/components/AdminPagesLayout';
import ListingsPage from 'src/admin/listings/components/ListingsPage';

// imports for `getServerSideProps`
import serverGetUser from 'src/common/utils/serverGetUser';
import handleApiErrors from 'src/common/utils/handleApiErrors';
import withCommonMiddleware from 'src/common/utils/withCommonMiddleware';
import userHasSession from 'src/common/utils/userHasSession';
import ListingsPageProvider, { Sort } from 'src/admin/listing-editor/providers/ListingsPageProvider';
import serverGetEditorListings, { ListingPagination } from 'src/admin/common/utils/serverGetEditorListings';
import { redirectInvalidOwner, redirectInvalidSession, redirectInvalidUser } from 'src/common/utils/sessionValidationRedirects';
import ensureString from 'src/common/utils/ensureString';
import { loadTranslations } from 'src/common/utils/serverSidePropsHelpers';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import AdminPagesProvider from 'src/admin/common/components/AdminPagesLayout/AdminPagesProvider';
import getCookie from 'src/common/utils/getCookie';
import { IMPERSONATED_SESSION_COOKIE_NAME } from 'src/common/constants';

type ListingsProps = {
  user: Partial<AuthUserApiResponse>;
  listings: EditorListing[];
  pagination: ListingPagination;
  search: string;
  sort: Sort;
  isImpersonatedSession: boolean;
};

const Listings: NextPage<ListingsProps> = ({
  listings, pagination, search, sort, isImpersonatedSession,
}) => {
  const [t] = useTranslation('admin-listings');
  const isNavUpdateEnabled = useFeatureFlag('pricing-v2--nav');

  return (
    <AdminPagesProvider>
      <Head>
        <title>{t(isNavUpdateEnabled ? 'new-title' : 'title')}</title>
      </Head>

      <AdminPagesLayout>
        <ListingsPageProvider value={{
          listings, pagination, search, sort, isImpersonatedSession,
        }}
        >
          <ListingsPage />
        </ListingsPageProvider>
      </AdminPagesLayout>
    </AdminPagesProvider>
  );
};

type ListingParams = {};
type ListingQuery = {
  search?: string;
  sort?: string;
};

export const getServerSideProps = handleApiErrors(
  withCommonMiddleware<ListingsProps, ListingParams, ListingQuery>(async (context) => {
    // Unauthenticated - redirect to login
    if (!userHasSession(context)) {
      return redirectInvalidSession(context);
    }

    const translations = loadTranslations(
      context.locale,
      ['common', 'admin-common', 'admin-listings', 'navbar'],
    );

    const user = await serverGetUser(context);

    const isImpersonatedSession = Boolean(getCookie(context, IMPERSONATED_SESSION_COOKIE_NAME));
    // The user must have an invalid session id - redirect to login
    if (!user.id) {
      return redirectInvalidUser(context);
    }

    // Redirect the user to the external onboarding process.
    if (!user.owner_approved) {
      return redirectInvalidOwner(user);
    }

    const { listings, pagination } = await serverGetEditorListings(context);

    // Render listings page for owners
    return {
      props: {
        user,
        listings,
        pagination,
        search: ensureString(context.query.search),
        sort: ensureString(context.query.sort) as Sort || 'new',
        isImpersonatedSession,
        ...(await translations),
      },
    };
  }),
);

export default Listings;
